import Link from 'next/link'
import React, { HTMLAttributes, ReactNode, createElement } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import clsx from 'clsx'

export interface ActionLinkProps extends HTMLAttributes<HTMLButtonElement & HTMLAnchorElement> {
  /**
   * The HTML Tag to render, defaults to 'button'
   */
  as?: string
  /**
   * Link text
   */
  children?: ReactNode
  /**
   * Link icon
   */
  icon?: IconDefinition
  /**
   * Position of the icon next to the text
   */
  iconPosition?: 'left' | 'right'
  /**
   * If `true`, the button will be disabled.
   */
  disabled?: boolean
  /**
   * Makes ActionLink an anchor HTML element
   */
  href?: string
  /**
   * Defines target for an href
   */
  hrefTarget?: '_blank' | '_self'
  /**
   * Defines rel for an href
   */
  rel?: string
  /**
   * Defines rel for an href
   */
  theme?: 'default' | 'white' | 'black' | 'neutral' | 'teal'
  /**
   * Shim html button which should come from attrs but isn't?
   */
  type?: 'button' | 'submit'
  /**
   * Add optional data-testid attribute
   */
  dataTestId?: string
}

const styles = {
  _: 'inline-flex items-center text-sm hover:underline font-medium',
  disabled: 'pointer-events-none cursor-not-allowed opacity-20',
  iconPosition: {
    left: 'order-first mr-2',
    right: 'order-last ml-2'
  },
  theme: {
    black: 'text-black hover:text-gray-600 active:text-gray-600',
    default: 'text-primary-default hover:text-primary-hover active:text-primary-pressed',
    neutral: 'text-blue-800 hover:text-blue-600 active:text-blue-600',
    teal: 'text-teal-400 hover:text-teal-500 active:text-teal-500',
    white: 'text-white hover:text-gray-300 active:text-gray-300'
  }
}

export function ActionLink({
  as = 'button',
  children,
  className,
  dataTestId,
  disabled,
  href,
  hrefTarget = '_self',
  icon,
  iconPosition = 'right',
  rel,
  theme = 'default',
  ...props
}: ActionLinkProps) {
  if (href)
    return (
      <Link
        className={clsx(className, styles._, disabled && styles.disabled, styles.theme[theme])}
        data-testid={dataTestId}
        href={href}
        onClick={event => props.onClick?.(event as any)}
        rel={rel}
        target={hrefTarget}
        {...props}
      >
        {icon ? <FontAwesomeIcon className={styles.iconPosition[iconPosition]} icon={icon} size='lg' /> : null}
        {children}
      </Link>
    )
  else
    return createElement(
      as,
      {
        className: clsx(className, styles._, disabled && styles.disabled, styles.theme[theme]),
        ...props
      },
      <>
        {icon ? <FontAwesomeIcon className={styles.iconPosition[iconPosition]} icon={icon} size='lg' /> : null}
        {children}
      </>
    )
}
